<template>
  <div data-testid="ar-dashboard-tenant-call-list">
    <v-card outlined class="outlined mb-6 elevation-0 rounded-lg">
          <v-toolbar
      color="transparent"
      flat
      class="text-h5 mb-2 rounded-0 py-1"
    >
        <span>A/R Tenant Call List</span>
        <v-spacer />
        <calls-filter
          @filtersApplied="applyFilters($event)"
          @clearBilling="billing_status = ''"
          @clearNameOrPhone="search_term = ''"
        />
      </v-toolbar>
      <v-data-table
        :headers="headers"
        :items="callList"
        :server-items-length="totalItems"
        :options.sync="pagination"
        :footer-props="{'items-per-page-options': itemsPerPageOptions}"
        :loading="loading"
        no-data-text="No tenants found."
      >
        <template slot="item" slot-scope="props">
          <tr>
          <td data-testid="full-name">{{ props.item.name }}</td>
          <td data-testid="location-code">{{ props.item.facility_location_code }}</td>
          <td data-testid="unit-number">{{ props.item.unit_name }}</td>
          <td data-testid="days-late">{{ props.item.days_late }}</td>
          <td data-testid="phone-home">
            <span v-if="props.item.phone_home !== null">
              {{ formatPhone(props.item.phone_home) }}
            </span>
            <span v-else>N/A</span>
          </td>
          <td data-testid="phone-mobile">
            <span v-if="props.item.phone_mobile !== null">
               {{ formatPhone(props.item.phone_mobile) }}
            </span>
            <span v-else> N/A</span>
          </td>
          <td data-testid="balance-due">${{ props.item.balance_due.toFixed(2) }}</td>
          <td data-testid="autobill-enabled">
            <span v-if="props.item.autobill_enabled" class="success--text">Enabled</span>
            <span v-else class="error--text">Disabled</span>
          </td>
          <td class="pa-0">
            <v-btn
              data-testid="make-call-btn"
              class="make-call-btn primary mx-2"
              small
              @click="openCallModal(props.item)"
            >
              Call
            </v-btn>
            <v-btn
              data-testid="skip-call-btn"
              class="skip-call-btn warning mx-2"
              small
              @click="openSkipModal(props.item)"
            >
              Skip
            </v-btn>
          </td>
          </tr>
        </template>
      </v-data-table>
    </v-card>

    <application-modal v-model="callModal" @cancel="callModal = false">
      <div slot="modal-title">Initiate A/R Tenant Call</div>
      <div slot="modal-body" style="min-height: 25rem">
        <call-modal-content
          v-if="callModal && callData"
          :callData="callData"
          v-model="callId"
        />
      </div>
      <v-btn
        data-testid="begin-call-btn"
        class="begin-call-btn primary"
        slot="create-button"
        :disabled="!callId"
        :loading="loading"
        @click="beginCall"
      >
        Begin Call
      </v-btn>
    </application-modal>

    <application-modal v-model="skipCallModal" @cancel="skipCallModal = false">
      <div slot="modal-title">Skip Tenant Call?</div>
      <div slot="modal-body">
        Press "Confirm" to remove
        <span class="info--text" data-testid="skip-name">{{ skippedTenantName }}</span>
        from the A/R Call List for today.
      </div>
      <v-btn
        data-testid="skip-tenant-btn"
        class="skip-tenant-btn primary"
        slot="create-button"
        @click="handleSkipTenant"
      >
        Confirm
      </v-btn>
    </application-modal>
  </div>
</template>

<script>
import ApplicationModal from "@/components/ApplicationModal";
import CallModalContent from "@/components/CallModalContent";
import CallsFilter from "./CallsFilter";
import { mapState } from "vuex";

export default {
  name: "CallsTable",
  components: { CallsFilter, CallModalContent, ApplicationModal },
  data() {
    return {
      billing_status: "",
      callData: "",
      callId: "",
      callModal: false,
      headers: [
        {
          text: "Name",
          value: "name",
          sortable: true
        },
        {
          text: "Facility",
          value: "facility_location_code",
          sortable: true
        },
        {
          text: "Unit #",
          value: "unit_name"
        },
        {
          text: "Days Late",
          value: "days_late",
          sortable: true
        },
        {
          text: "Home Phone #",
          value: "phone_home",
          sortable: false
        },
        {
          text: "Mobile Phone #",
          value: "phone_mobile",
          sortable: false
        },
        {
          text: "Balance",
          value: "balance_due",
          sortable: true
        },
        {
          text: "Autobill",
          value: "autobill_enabled",
          sortable: true
        },
        {
          text: "Actions",
          sortable: false,
          class: "pl-2"
        }
      ],
      itemsPerPageOptions: [10, 25, 50, 100],
      loading: false,
      nameOrPhoneFilter: "",
      pagination: {
        page: 1,
        itemsPerPage: 10,
        sortBy: ["name"],
        sortDesc: [false]
      },
      skipCallLoading: false,
      skipCallModal: false,
      skippedTenantName: "",
      skippedTenantId: "",
      search: "",
      searchOn: false,
      search_term: "",
      totalItems: 0
    };
  },
  methods: {
    openCallModal(callData) {
      this.callModal = true;
      this.callData = callData;
    },
    openSkipModal(callData) {
      this.skippedTenantName = callData.name;
      this.skippedTenantId = callData.tenant_id;
      this.skipCallModal = true;
    },
    handleSkipTenant() {
      this.skipCallLoading = true;
      this.$store.dispatch("markTenantSkipped", this.skippedTenantId)
        .then(() => {
          this.$store.dispatch("handleSuccess", this.skippedTenantName + " marked as skipped.");
          this.skipCallLoading = false;
          this.skipCallModal = false;
          this.skippedTenantName = "";
          this.skippedTenantId = "";
          this.retrieveTenantsToBeCalled();
        })
        .catch(err => {
          this.$store.dispatch("handleError", err);
          this.skipCallLoading = false;
        });
    },
    beginCall() {
      let createCallPayload = {
        ctm_id: parseFloat(this.callId),
        call_type: "accounts-receivable",
        location_code: this.callData.facility_location_code,
        tenant_id: this.callData.tenant_id
      };
      // this.loading = true;

      this.$store.dispatch("beginCall", createCallPayload)
        .then(resp => {
          let routeCallPayload = {
            locationCode: null,
            reservationId: null,
            callId: resp.data.call.id,
            ctm_id: createCallPayload.ctm_id,
            call_type: createCallPayload.call_type,
            router: {
              tenantId: createCallPayload.tenant_id,
              facilityId: this.callData.facility_id
            },
            contact_id: null
          };
          this.$store.commit("setArData");
          this.$store.dispatch("routeCall", routeCallPayload);
        })
        .catch(err => {
          this.$store.dispatch("handleError", err);
          this.loading = false;
        });
    },
    retrieveTenantsToBeCalled(resetPagination = false) {
      this.loading = true;

      /**
       * Related bug:
       * https://storespace.atlassian.net/browse/SS-4668
       * https://github.com/vuetifyjs/vuetify/issues/10949
       */
      this.pagination.page = resetPagination ? 1 : this.pagination.page;

      const params = {
        page: this.pagination.page,
        per_page: this.pagination.itemsPerPage,
        sort_by: this.pagination.sortBy[0] ? this.pagination.sortBy[0] : "",
        order: this.pagination.sortDesc[0] ? "desc" : "asc",
        billing_status: this.billing_status,
        search_term: this.search_term
      };

      if (this.searchOn) {
        params.search_term = this.search;
      }

      this.$store.dispatch("getTenantsToBeCalled", params)
        .then(resp => {
          this.totalItems = resp.data.meta.total;
          this.loading = false;
        })
        .catch(err => {
          this.$store.dispatch("handleError", err);
          this.loading = false;
        });
    },
    applyFilters(filter) {
      this.billing_status = filter.billingStatus;
      this.search_term = filter.nameOrPhone;
      this.retrieveTenantsToBeCalled(true);
    },
    formatPhone(phone) {
      if (phone) {
        if (phone.length === 10 && typeof phone.replace === "function") {
          return phone.replace(/(\d{3})(\d{3})(\d{4})/, "$1-$2-$3");
        } else {
          return phone;
        }
      }
    }
  },

  computed: {
    ...mapState({
      callList: state => state.accountsReceivable.tenantsToBeCalled
    })
  },

  watch: {
    pagination: {
      handler() {
        this.retrieveTenantsToBeCalled();
      },
      deep: true
    }
  },

  created() {
    this.retrieveTenantsToBeCalled();
  }
};
</script>
